// src/stores/userChatStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  updateDoc,
  getDocs,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/services/firebaseconfig'
import { playChatNotificationTone } from '@/utils/audioTones'

export const useUserChatStore = defineStore('userChat', () => {
  // ═══════════════════════════════════════════════════════════════════
  // State
  // ═══════════════════════════════════════════════════════════════════

  const myUid = ref(null)
  const myName = ref('')

  // Track if user is actively viewing chat (to suppress notification sounds)
  const isChatTabActive = ref(false)

  // All chat threads for current user
  const threads = ref([]) // Array of { chatId, recipientUid, recipientName, lastMessage, lastMessageAt, unreadCount }
  const threadsUnsubscribe = ref(null)

  // Currently active chat
  const activeChatId = ref(null)
  const activeChatRecipient = ref(null) // { uid, name }
  const messages = ref([]) // Messages in active chat
  const messagesUnsubscribe = ref(null)

  // Typing indicators
  const typingUsers = ref({}) // { chatId: { uid: timestamp } }
  const typingUnsubscribe = ref(null)

  // Total unread count across all chats
  const totalUnreadCount = computed(() => {
    return threads.value.reduce((sum, t) => sum + (t.unreadCount || 0), 0)
  })

  // ── 3WzEAkiv — per-user thread archive ──────────────────────────────
  // Archive is a flag on the VIEWER's own thread doc (userChats/{myUid}/threads),
  // so it never affects the other participant (they have their own copy). It's a
  // reversible view-state, not a delete. An archived thread auto-resurfaces into
  // the active list when a NEW message arrives after it was archived
  // (lastMessageAt > archivedAt) — purely reactive, no writes.
  function _ms(ts) {
    if (!ts) return 0
    if (typeof ts.toMillis === 'function') return ts.toMillis()
    if (typeof ts.seconds === 'number') return ts.seconds * 1000
    return 0
  }
  function _isEffectivelyArchived(t) {
    if (!t || t.archived !== true) return false
    const archivedMs = _ms(t.archivedAt)
    const lastMs = _ms(t.lastMessageAt)
    // A message arrived after the archive moment → resurface (treat as active).
    if (archivedMs && lastMs && lastMs > archivedMs) return false
    return true
  }
  const activeThreads = computed(() => threads.value.filter(t => !_isEffectivelyArchived(t)))
  const archivedThreads = computed(() => threads.value.filter(t => _isEffectivelyArchived(t)))

  // ═══════════════════════════════════════════════════════════════════
  // Initialization
  // ═══════════════════════════════════════════════════════════════════

  async function initialize(uid, displayName) {
    myUid.value = uid
    myName.value = displayName

    // Ensure the user's chat document exists (required for subcollection queries)
    try {
      const userChatRef = doc(db, 'userChats', uid)
      await setDoc(
        userChatRef,
        {
          ownerId: uid,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      )
    } catch (e) {
      console.warn('[UserChatStore] Could not initialize user chat doc:', e)
    }

    // Listen to all chat threads for this user
    startThreadsListener()

    console.log('[UserChatStore] Initialized for:', uid)
  }

  function cleanup() {
    stopThreadsListener()
    stopMessagesListener()
    stopTypingListener()
    // Detach the visibilitychange listener — without this, logout + re-login
    // (or HMR in dev) leaves a stale listener attached holding a closed-over
    // `myUid` that no longer matches the new session, and each subsequent
    // session adds another. Pattern: store the bound handler ref, remove it.
    if (typeof document !== 'undefined' && _visibilityHandler) {
      document.removeEventListener('visibilitychange', _visibilityHandler)
      _visibilityHandler = null
    }
    // Reset tracking
    previousTotalUnread = 0
    isFirstLoad = true
    // Back to the state before initialize(): App.vue re-initialises only when myUid is empty, so an identity left
    // behind keeps the next sign-in on the last user's chats (docs/bugs/walkie_call_lifecycle_BUG.md).
    myUid.value = null
    myName.value = ''
  }

  // Module-scoped reference so cleanup() can detach. Initialized below where
  // the addEventListener call lives.
  let _visibilityHandler = null

  // ═══════════════════════════════════════════════════════════════════
  // Chat ID Helper
  // ═══════════════════════════════════════════════════════════════════

  function getChatId(uid1, uid2) {
    // Consistent chat ID regardless of who initiates
    return [uid1, uid2].sort().join('_')
  }

  // ═══════════════════════════════════════════════════════════════════
  // Threads Listener
  // ═══════════════════════════════════════════════════════════════════

  // Track previous unread count to detect new messages
  let previousTotalUnread = 0
  let isFirstLoad = true

  function startThreadsListener() {
    if (!myUid.value) return

    const threadsRef = collection(db, 'userChats', myUid.value, 'threads')
    const q = query(threadsRef, orderBy('lastMessageAt', 'desc'))

    threadsUnsubscribe.value = onSnapshot(
      q,
      snapshot => {
        const newThreads = snapshot.docs.map(d => ({
          chatId: d.id,
          ...d.data(),
        }))

        // Calculate new total unread
        const newTotalUnread = newThreads.reduce((sum, t) => sum + (t.unreadCount || 0), 0)

        // Play sound if unread count increased (and not first load)
        // Don't play if user has the chat tab open
        if (!isFirstLoad && newTotalUnread > previousTotalUnread && !isChatTabActive.value) {
          // Check if message is from someone else (not our own sent message)
          const latestThread = newThreads[0]
          if (latestThread && latestThread.lastMessageBy !== myUid.value) {
            playChatNotificationTone()
          }
        }

        previousTotalUnread = newTotalUnread
        isFirstLoad = false
        threads.value = newThreads
      },
      error => {
        // Ignore permission errors on empty collections - this is expected
        // when the user has no chat history yet
        if (error.code === 'permission-denied') {
          console.log('[UserChatStore] No chat history yet (collection may not exist)')
          threads.value = []
        } else {
          console.error('[UserChatStore] Threads listener error:', error)
        }
      }
    )
  }

  function stopThreadsListener() {
    if (threadsUnsubscribe.value) {
      threadsUnsubscribe.value()
      threadsUnsubscribe.value = null
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Messages Listener (for active chat)
  // ═══════════════════════════════════════════════════════════════════

  function openChat(recipientUid, recipientName) {
    const chatId = getChatId(myUid.value, recipientUid)

    activeChatId.value = chatId
    activeChatRecipient.value = { uid: recipientUid, name: recipientName }

    // Stop previous listener
    stopMessagesListener()

    // Start new listener
    const messagesRef = collection(db, 'userChats', myUid.value, 'threads', chatId, 'messages')

    // Get the newest 100 messages (prevents the thread from "stopping" at 100)
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(100))

    messagesUnsubscribe.value = onSnapshot(
      q,
      snapshot => {
        // Reverse back to chronological order for the UI
        messages.value = snapshot.docs
          .map(d => ({
            id: d.id,
            ...d.data(),
          }))
          .reverse()

        // Mark messages as read
        markMessagesAsRead(chatId)
      },
      error => {
        console.error('[UserChatStore] Messages listener error:', error)
      }
    )

    // Start typing listener
    startTypingListener(chatId)
  }

  function closeChat() {
    stopMessagesListener()
    stopTypingListener()
    activeChatId.value = null
    activeChatRecipient.value = null
    messages.value = []
  }

  function stopMessagesListener() {
    if (messagesUnsubscribe.value) {
      messagesUnsubscribe.value()
      messagesUnsubscribe.value = null
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Send Message (with optional attachment)
  // ═══════════════════════════════════════════════════════════════════

  async function sendMessage(text, attachment = null) {
    if ((!text || !text.trim()) && !attachment) return
    if (!activeChatId.value || !activeChatRecipient.value) return

    const chatId = activeChatId.value
    const recipientUid = activeChatRecipient.value.uid
    const recipientName = activeChatRecipient.value.name
    const trimmedText = text?.trim() || ''

    // All writes go through the `sendChatMessage` callable. The server
    // validates `users/{recipientUid}` exists before creating a thread —
    // without this gate, sending to a stale UID silently succeeds and
    // the message vanishes (Sunna's 2026-04-26 incident: 5 chats sent
    // to a wrong UID; Erik never saw any).
    const sendFn = httpsCallable(functions, 'sendChatMessage')
    try {
      await sendFn({
        recipientUid,
        recipientName,
        chatId,
        text: trimmedText,
        senderName: myName.value,
        ...(attachment
          ? {
              attachment: {
                url: attachment.url,
                name: attachment.name,
                type: attachment.type,
                size: attachment.size,
              },
            }
          : {}),
      })
      clearTypingIndicator()
    } catch (error) {
      console.error('[UserChatStore] Send message error:', error)
      throw error
    }
  }

  async function _getUnreadCount(uid, chatId) {
    try {
      const threadSnap = await getDocs(
        query(collection(db, 'userChats', uid, 'threads'), where('__name__', '==', chatId))
      )
      if (!threadSnap.empty) {
        return threadSnap.docs[0].data().unreadCount || 0
      }
    } catch (e) {
      // Ignore
    }
    return 0
  }

  // ═══════════════════════════════════════════════════════════════════
  // Delete Message
  // ═══════════════════════════════════════════════════════════════════

  async function deleteMessage(messageId) {
    if (!activeChatId.value || !activeChatRecipient.value || !messageId) return

    const chatId = activeChatId.value
    const recipientUid = activeChatRecipient.value.uid

    try {
      // Delete from both users' message collections
      const myMessageRef = doc(
        db,
        'userChats',
        myUid.value,
        'threads',
        chatId,
        'messages',
        messageId
      )
      const recipientMessageRef = doc(
        db,
        'userChats',
        recipientUid,
        'threads',
        chatId,
        'messages',
        messageId
      )

      await Promise.all([deleteDoc(myMessageRef), deleteDoc(recipientMessageRef)])

      console.log('[UserChatStore] Message deleted:', messageId)
    } catch (error) {
      console.error('[UserChatStore] Delete message error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Reactions
  // ═══════════════════════════════════════════════════════════════════

  async function addReaction(messageId, emoji) {
    if (!activeChatId.value || !activeChatRecipient.value || !messageId) return

    const chatId = activeChatId.value
    const recipientUid = activeChatRecipient.value.uid

    try {
      // Update reactions in both users' message docs
      const myMessageRef = doc(
        db,
        'userChats',
        myUid.value,
        'threads',
        chatId,
        'messages',
        messageId
      )
      const recipientMessageRef = doc(
        db,
        'userChats',
        recipientUid,
        'threads',
        chatId,
        'messages',
        messageId
      )

      // Use arrayUnion to add user to reaction
      const update = {
        [`reactions.${emoji}`]: arrayUnion(myUid.value),
      }

      await Promise.all([updateDoc(myMessageRef, update), updateDoc(recipientMessageRef, update)])

      console.log('[UserChatStore] Reaction added:', emoji)
    } catch (error) {
      console.error('[UserChatStore] Add reaction error:', error)
      throw error
    }
  }

  async function removeReaction(messageId, emoji) {
    if (!activeChatId.value || !activeChatRecipient.value || !messageId) return

    const chatId = activeChatId.value
    const recipientUid = activeChatRecipient.value.uid

    try {
      // Update reactions in both users' message docs
      const myMessageRef = doc(
        db,
        'userChats',
        myUid.value,
        'threads',
        chatId,
        'messages',
        messageId
      )
      const recipientMessageRef = doc(
        db,
        'userChats',
        recipientUid,
        'threads',
        chatId,
        'messages',
        messageId
      )

      // Use arrayRemove to remove user from reaction
      const update = {
        [`reactions.${emoji}`]: arrayRemove(myUid.value),
      }

      await Promise.all([updateDoc(myMessageRef, update), updateDoc(recipientMessageRef, update)])

      console.log('[UserChatStore] Reaction removed:', emoji)
    } catch (error) {
      console.error('[UserChatStore] Remove reaction error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Mark as Read
  // ═══════════════════════════════════════════════════════════════════

  async function markMessagesAsRead(chatId) {
    if (!myUid.value) return

    try {
      // Reset unread count on my thread
      const threadRef = doc(db, 'userChats', myUid.value, 'threads', chatId)
      await updateDoc(threadRef, { unreadCount: 0 })
    } catch (error) {
      // Thread might not exist yet, ignore
    }
  }

  // ── 3WzEAkiv — archive / unarchive a thread (this viewer only) ──
  async function archiveThread(chatId) {
    if (!myUid.value || !chatId) return
    try {
      const threadRef = doc(db, 'userChats', myUid.value, 'threads', chatId)
      // Clear unread on archive — archiving implies dismissing, and it keeps
      // totalUnreadCount from counting a now-hidden thread.
      await updateDoc(threadRef, {
        archived: true,
        archivedAt: serverTimestamp(),
        unreadCount: 0,
      })
    } catch (error) {
      console.error('[UserChatStore] archiveThread error:', error)
      throw error
    }
  }

  async function unarchiveThread(chatId) {
    if (!myUid.value || !chatId) return
    try {
      const threadRef = doc(db, 'userChats', myUid.value, 'threads', chatId)
      await updateDoc(threadRef, { archived: false, archivedAt: null })
    } catch (error) {
      console.error('[UserChatStore] unarchiveThread error:', error)
      throw error
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Typing Indicators
  // ═══════════════════════════════════════════════════════════════════

  function startTypingListener(chatId) {
    stopTypingListener()

    const typingRef = doc(db, 'userChatTyping', chatId)

    typingUnsubscribe.value = onSnapshot(typingRef, snapshot => {
      if (snapshot.exists()) {
        const data = snapshot.data()
        typingUsers.value[chatId] = data
      } else {
        typingUsers.value[chatId] = {}
      }
    })
  }

  function stopTypingListener() {
    if (typingUnsubscribe.value) {
      typingUnsubscribe.value()
      typingUnsubscribe.value = null
    }
  }

  let typingTimeout = null

  async function setTypingIndicator() {
    if (!activeChatId.value || !myUid.value) return

    try {
      const typingRef = doc(db, 'userChatTyping', activeChatId.value)
      await setDoc(
        typingRef,
        {
          [myUid.value]: serverTimestamp(),
        },
        { merge: true }
      )

      // Auto-clear after 3 seconds
      if (typingTimeout) clearTimeout(typingTimeout)
      typingTimeout = setTimeout(() => {
        clearTypingIndicator()
      }, 3000)
    } catch (e) {
      // Ignore
    }
  }

  async function clearTypingIndicator() {
    if (!activeChatId.value || !myUid.value) return

    try {
      const typingRef = doc(db, 'userChatTyping', activeChatId.value)
      await setDoc(
        typingRef,
        {
          [myUid.value]: null,
        },
        { merge: true }
      )
    } catch (e) {
      // Ignore
    }

    if (typingTimeout) {
      clearTimeout(typingTimeout)
      typingTimeout = null
    }
  }

  const isRecipientTyping = computed(() => {
    if (!activeChatId.value || !activeChatRecipient.value) return false

    const chatTyping = typingUsers.value[activeChatId.value]
    if (!chatTyping) return false

    const recipientTypingAt = chatTyping[activeChatRecipient.value.uid]
    if (!recipientTypingAt) return false

    // Check if typing indicator is recent (within last 4 seconds)
    const typingTime = recipientTypingAt?.toDate?.() || new Date(recipientTypingAt)
    const now = new Date()
    return now - typingTime < 4000
  })

  // ═══════════════════════════════════════════════════════════════════
  // Start Chat (from user list or walkie)
  // ═══════════════════════════════════════════════════════════════════

  async function startChatWithUser(recipientUid, recipientName) {
    openChat(recipientUid, recipientName)
  }

  // ═══════════════════════════════════════════════════════════════════
  // Chat Tab Active State (for suppressing notification sounds)
  // ═══════════════════════════════════════════════════════════════════

  function setChatTabActive(active) {
    isChatTabActive.value = active
  }

  // ═══════════════════════════════════════════════════════════════════
  // Visibility-aware re-subscribe
  // ═══════════════════════════════════════════════════════════════════
  // Firestore's onSnapshot websocket can silently drop after long tab-idle
  // / sleep / network blip. The SDK is supposed to reconnect, but in
  // practice listeners sometimes stop firing until the page is refreshed —
  // resulting in a stale chat UI (no badges, no audio alerts, no new
  // messages visible) even though the underlying writes are landing in
  // Firestore. Workaround: when the tab regains focus, tear down and
  // re-establish both the threads listener and the active-chat messages
  // listener.
  if (typeof document !== 'undefined') {
    // Store the handler in module scope so cleanup() can detach it on
    // logout / HMR. An anonymous arrow couldn't be removed and silently
    // accumulated one listener per session, each holding a stale closure.
    _visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return
      if (!myUid.value) return // not initialized yet
      try {
        stopThreadsListener()
        startThreadsListener()
        if (activeChatId.value && activeChatRecipient.value) {
          // openChat() handles stop+start of the messages listener and
          // resyncs typing indicators in one call.
          openChat(activeChatRecipient.value.uid, activeChatRecipient.value.name)
        }
      } catch (e) {
        console.warn('[UserChatStore] visibilitychange re-subscribe failed:', e?.message)
      }
    }
    document.addEventListener('visibilitychange', _visibilityHandler)
  }

  // ═══════════════════════════════════════════════════════════════════
  // Return
  // ═══════════════════════════════════════════════════════════════════

  return {
    // State
    myUid,
    myName,
    threads,
    activeThreads,
    archivedThreads,
    activeChatId,
    activeChatRecipient,
    messages,
    totalUnreadCount,
    isRecipientTyping,
    isChatTabActive,

    // Actions
    initialize,
    cleanup,
    openChat,
    closeChat,
    sendMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    setTypingIndicator,
    clearTypingIndicator,
    startChatWithUser,
    getChatId,
    setChatTabActive,
    archiveThread,
    unarchiveThread,
  }
})
